/**
 * @category 0.Pinia Stores
 * @subcategory AGENT
 * @module AgentStore
 */
import { defineStore, storeToRefs } from 'pinia';
import { computed, ref, type Ref } from 'vue';
import type { IAgent, IAgentConfiguration } from '@/types/agent';
import { getKPI } from '@/api/kpi';
import { fromSecondsToDate } from '@/utils/common';
import { t } from '@/main';
import { getRecording } from '@/api/audioChat';
import { useContactStore } from '@/stores/contact';
import { useContactHistoryStore } from '@/stores/contactHistory';
import { getUserById } from '@/api/serviceNow';
import { getSupportEnabled } from '@/api/support';
import { FEATURE_SHOW_CONSOLE_COMANDI } from '@/constants/featureFlags';
import { useRolesStore } from '@/stores/roles';

type ConnectTags = Record<string, unknown>;
type UserInfoWithTags = {
  Tags?: ConnectTags;
  tags?: ConnectTags;
  UserTags?: ConnectTags;
  userTags?: ConnectTags;
  POLO?: string | string[];
  POLOs?: string[];
  polo?: string | string[];
  polos?: string[];
};

const isConnectTags = (value: unknown): value is ConnectTags => {
  return !!value && typeof value === 'object' && !Array.isArray(value);
};

const getFirstTagsValue = (source: unknown): ConnectTags | string | string[] | null => {
  if (!source || typeof source !== 'object') {
    return null;
  }

  const data = source as Record<string, unknown>;
  const tagFields = [
    'Tags',
    'tags',
    'UserTags',
    'userTags',
    'ResourceTags',
    'resourceTags',
    'POLO',
    'POLOs',
    'polo',
    'polos'
  ];

  for (const field of tagFields) {
    const value = data[field];

    if (value) {
      return value as ConnectTags | string | string[];
    }
  }

  return null;
};

const getConnectTags = (agent: connect.Agent, agentConfiguration: IAgentConfiguration): ConnectTags | string | string[] | null => {
  const agentData = (agent as connect.Agent & { _getData?: () => unknown })._getData?.();
  const config = agentConfiguration as IAgentConfiguration & {
    agentPreferences?: Record<string, unknown>;
  };

  return getFirstTagsValue(agentConfiguration) ||
    getFirstTagsValue(config.agentPreferences) ||
    getFirstTagsValue(agentData);
};

const getPoloFromUserInfo = (userInfo: unknown): string | string[] | ConnectTags | null => {
  const data = userInfo as UserInfoWithTags;

  return data.Tags || data.tags || data.UserTags || data.userTags || data.POLOs || data.polos || data.POLO || data.polo || null;
};

export const useAgentStore = defineStore('agent', () => {
  const agentState: Ref<IAgent> = ref({
    agent: {},
    agentConfiguration: { username: '', name: '', routingProfile: {} },
    state: { type: '' }
  });
  const agentToggleState = ref(false);
  const agentFrazionario = ref('');
  const agentCodUser = ref('');
  const agentKpiState = ref({
    AVG_HANDLE_TIME: 0,
    CONTACTS_HANDLED: 0,
    AGENT_NON_RESPONSE: 0
  });
  const serviceAreaKpiState = ref({
    SERVICE: 0,
    AREA: 0,
    CLUSTER: 0
  });
  const lineDropDisabledState = ref(true);
  const supportEnabled = ref(false);

  const showConsoleComandi = computed(() => FEATURE_SHOW_CONSOLE_COMANDI);

  /**
   * @function agentGetter computed
   * @returns {Array.<Object>}
   * @property {string} key I18n key
   * @property {string} value Logged agent informations
   * @desc Access *agentState* to return an array with the informations of the logged-in agent
   *
   * **Invoked by:**
   * - ##### [UserPopup.vue](module-UserPopup.html)
   */
  const agentGetter = computed(() => {
    const { agentConfiguration } = agentState.value;
    const { username, name, routingProfile } = agentConfiguration as IAgentConfiguration;

    let agentGetter = [
      { key: t('agent_popup.username'), value: '' },
      { key: t('agent_popup.name'), value: '' },
      { key: t('agent_popup.agent_id'), value: '' },
      { key: t('agent_popup.profile'), value: '' },
      { key: t('agent_popup.state'), value: '' }
    ];

    if (routingProfile?.queues?.length > 0) {
      const { agentARN } = agentConfiguration as IAgentConfiguration;

      const agentId = (agentARN as string).split('/').reverse()[0];

      agentGetter = [
        { key: t('agent_popup.username'), value: username },
        { key: t('agent_popup.name'), value: name },
        { key: t('agent_popup.agent_id'), value: agentId },
        { key: t('agent_popup.profile'), value: routingProfile.name }
      ];
    }

    return agentGetter;
  });

  /**
   * @function agentKpiGetter computed
   * @returns {Array.<Object>}
   * @property {string} tooltip Icon tooltip
   * @property {number} value Icon value
   * @property {string} icon Icon name
   * @property {string} iconColor Ion color
   * @desc Access *agentKpiState* to return an array with an object for each kpi icon
   *
   * **Invoked by:**
   * - ##### [AgentKPI.vue](module-component-AgentKPI.html)
   */
  const agentKpiGetter = computed((
  ) => {
    const { AVG_HANDLE_TIME, CONTACTS_HANDLED, AGENT_NON_RESPONSE } = agentKpiState.value;

    const { time } = fromSecondsToDate(AVG_HANDLE_TIME);

    return [
      { tooltip: 'call_agent_metrics.managed', value: CONTACTS_HANDLED, icon: 'ICONS.ACCOUNT', iconColor: 'green-9' },
      { tooltip: 'call_agent_metrics.lost', value: AGENT_NON_RESPONSE, icon: 'ICONS.ACCOUNT_OFF_OUTLINE', iconColor: 'red-8' },
      { tooltip: 'call_agent_metrics.talk_time', value: time, icon: 'ICONS.ACCOUNT_CLOCK_OUTLINE', iconColor: 'indigo-8' }
    ];
  });

  /**
   * @function serviceAreaKpiGetter computed
   * @returns {Array.<Object>}
   * @property {string} tooltip Icon tooltip
   * @property {string} value Icon value
   * @property {string} icon Icon name
   * @property {string} iconColor Icon color
   * @desc Access *serviceAreaKpiState* to return an array with an object for each service area kpi icon
   *
   * **Invoked by:**
   * - ##### [ServiceAreaKPI.vue](module-component-ServiceAreaKPI.html)
   */
  const serviceAreaKpiGetter = computed(() => {
    const { SERVICE, AREA, CLUSTER } = serviceAreaKpiState.value;
    const { currentContactGetter } = storeToRefs(useContactStore());
    const currentContact = currentContactGetter.value;
    const service = currentContact?.service;
    const area = currentContact?.area;
    const matchingCluster = currentContact?.matchingCluster;

    return [
      { tooltip: `Contatore Service giornaliero${service ? ` - ${service}` : ''}`, value: SERVICE || '-', icon: 'ICONS.ACCOUNT_EYE', iconColor: 'green-9' },
      { tooltip: `Contatore Area giornaliero${area ? ` - ${area}` : ''}`, value: AREA || '-', icon: 'ICONS.ACCOUNT_EYE_OUTLINE', iconColor: 'red-8' },
      { tooltip: `Contatore Cluster giornaliero${matchingCluster ? ` - ${matchingCluster}` : ''}`, value: CLUSTER || '-', icon: 'ICONS.ACCOUNT_GROUP', iconColor: 'indigo-8' }
    ];
  });

  const setAgentAction = async (
    agent: connect.Agent
  ): Promise<void> => {
    if (agent.getState()?.type === connect.AgentStateType.ROUTABLE) {
      agentToggleState.value = true;
    }

    const agentConfiguration = agent.getConfiguration();
    console.log('Agent Routing Profile:', agentConfiguration.routingProfile);
    const connectTags = getConnectTags(agent, agentConfiguration);
    const rolesStore = useRolesStore();

    if (connectTags) {
      rolesStore.setPolo(connectTags);
    }

    console.group('Amazon Connect tags debug');
    console.log('agent username:', agentConfiguration.username);
    console.log('agent ARN:', agentConfiguration.agentARN);
    console.log('agent configuration:', agentConfiguration);
    console.log('connect tags:', connectTags);
    console.log('connect tags source includes object:', isConnectTags(connectTags));
    console.log('raw polo saved in store:', rolesStore.getPolo);
    console.log('calculated role:', rolesStore.getUserRole);
    console.log('isAdmin:', rolesStore.isAdmin);
    console.groupEnd();

    agentState.value = {
      agent,
      agentConfiguration,
      state: agent.getState()
    };

    // On every page load, fetch the current agent's contact history for the day
    // so the STORICO CONTATTI table is populated without waiting for the tab to open.
    useContactHistoryStore().loadContactHistory(agentConfiguration.username?.split('@')[0] || '');

    const rpId = agentConfiguration.routingProfile?.routingProfileARN?.split('/').pop();
    if (rpId) {
      try {
        const { enabled } = await getSupportEnabled(rpId);
        supportEnabled.value = enabled;
      } catch (error) {
        console.error('Error fetching support enabled status:', error);
        supportEnabled.value = false;
      }
    }
  };

  /**
   * @function setAgentKpiAction
   * @async
   * @desc Save the response of getKpi() inside *agentKpiState*
   *
   * **Invoked by:**
   * - ##### [contactOnMissed() | contactOnEnded() - classes/Contact.ts](module-ContactClass.html#~contactOnEnded)
   * - ##### [initAgent() - classes/Agent.ts](module-AgentClass.html#~initAgent)
   */
  const setAgentKpiAction = async (
  ) => {
    const { agentConfiguration } = agentState.value;
    const { agentARN } = agentConfiguration as IAgentConfiguration;

    const agentId = (agentARN as string).split('/').reverse()[0];

    const { getKpiResp } = await getKPI(agentId);

    agentKpiState.value = getKpiResp;
  };

  const updateRecordingTableAction = async (
  ) => {
    const { agentConfiguration } = agentState.value;
    const { username } = agentConfiguration as IAgentConfiguration;
    try {
      const { Items } = await getRecording(username.split('@')[0]);

      (agentConfiguration as IAgentConfiguration).recordingTableItems = Items.map(item => {
        const arr = item.link.split('?path=')[1].split('/');
        const username = arr[4];
        const frazionarioPdl = arr[5].split('-');
        const frazionario = frazionarioPdl[0];
        const pdl = frazionarioPdl[1];
        const [day, month, year] = arr.slice(1, 4).reverse();
        const timestamp = `${year}-${month}-${day} ${new Date(item.timestamp).toLocaleTimeString()}`;

        return {
          username,
          frazionario,
          pdl,
          link: item.link,
          timestamp,
          contactId: item.contactId
        };
      });

    } catch (error) {
      (agentConfiguration as IAgentConfiguration).recordingTableItems = [];
    }
  };

  const setAgentOnlineAction = async (
  ) => {
    const { agent } = agentState.value;

    const getAgentState = (
      agentStateType: string
    ) => {
      return (agent as connect.Agent).getAgentStates().find((
        { type }: {type: string}
      ) => type === agentStateType);
    };

    (agent as connect.Agent).setState(
            getAgentState(connect.AgentStateType.ROUTABLE) as connect.AgentStateDefinition
    );

    agentToggleState.value = true;
  };

  const setAgentOfflineAction = async (
  ) => {
    const { agent } = agentState.value;

    const getAgentState = (
      agentStateType: string
    ) => {
      return (agent as connect.Agent).getAgentStates().find((
        { type }: {type: string}
      ) => type === agentStateType);
    };

    (agent as connect.Agent).setState(
          getAgentState(connect.AgentStateType.OFFLINE) as connect.AgentStateDefinition
    );

    agentToggleState.value = false;
  };

  const changeAgentCallStateAction = (
  ): void => {
    const { agent } = agentState.value;

    const getAgentState = (
      agentStateType: string
    ) => {
      return (agent as connect.Agent).getAgentStates().find((
        { type }: {type: string}
      ) => type === agentStateType);
    };

    if (agentToggleState.value) {
      (agent as connect.Agent).setState(
        getAgentState(connect.AgentStateType.ROUTABLE) as connect.AgentStateDefinition
      );
    } else {
      const { agent } = agentState.value;
      (agent as connect.Agent).setState(
        getAgentState(connect.AgentStateType.OFFLINE) as connect.AgentStateDefinition
      );
    }
  };
  const fetchAgentUserDataAction = async () => {
    const { agentConfiguration } = agentState.value;
    const { username } = agentConfiguration as IAgentConfiguration;
    const userId = username.includes('@') ? username.split('@')[0] : username;
    const agentInfoById = await getUserById(userId);
    agentFrazionario.value = agentInfoById.Frazionario;
    agentCodUser.value = agentInfoById.CodUser;

    const rawPolo = getPoloFromUserInfo(agentInfoById);
    const rolesStore = useRolesStore();
    if (rawPolo) {
      rolesStore.setPolo(rawPolo);
    }

    console.group('Agent user polo debug');
    console.log('userId:', userId);
    console.log('getUserById response:', agentInfoById);
    console.log('raw polo/tags from getUserById:', rawPolo);
    console.log('raw polo saved in store:', rolesStore.getPolo);
    console.log('calculated role:', rolesStore.getUserRole);
    console.log('isAdmin:', rolesStore.isAdmin);
    console.groupEnd();
  };
  return {
    agentState,
    agentToggleState,
    agentKpiState,
    serviceAreaKpiState,
    agentFrazionario,
    agentCodUser,
    supportEnabled,
    showConsoleComandi,
    agentGetter,
    agentKpiGetter,
    serviceAreaKpiGetter,
    lineDropDisabledState,
    setAgentAction,
    setAgentKpiAction,
    updateRecordingTableAction,
    setAgentOnlineAction,
    setAgentOfflineAction,
    changeAgentCallStateAction,
    fetchAgentUserDataAction
  };
});
